import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { Progress } from "@/shared/components/ui/progress";
import {
  FileText,
  Server,
  Globe,
  Zap,
  Database,
  Terminal,
  MemoryStick,
} from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

const getTypeIcon = (type) => {
  const iconMap = {
    gateway: Globe,
    processor: Zap,
    database: Database,
    worker: Terminal,
    cache: MemoryStick,
  };
  return iconMap[type] || Server;
};

export default function ServerCard({
  server,
  statusConfig,
  index,
  onViewLogs,
  onSelect,
  translateServerName,
  translateRegion,
}) {
  const { t } = useLanguage();

  const displayStatus = !server.isDataReal ? "disabled" : server.status;
  const status = statusConfig[displayStatus];
  const StatusIcon = status.icon;
  const TypeIcon = getTypeIcon(server.type);

  return (
    <motion.div
      key={server.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`bg-white rounded-2xl p-5 shadow-sm border cursor-pointer hover:shadow-md transition-all ${
        server.status === "warning"
          ? "border-amber-200"
          : server.status === "critical"
            ? "border-red-200"
            : "border-slate-100"
      } ${!server.isDataReal ? "opacity-50" : ""}`}
      onClick={() => onSelect(server)}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-xl ${status.bg} flex items-center justify-center`}
          >
            <TypeIcon className={`h-5 w-5 ${status.color}`} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">
              {translateServerName(server.name)}
            </h3>
            <p className="text-xs text-slate-500">
              {translateRegion(server.region)} • {server.ip || t("na")}
            </p>
          </div>
        </div>
        <Badge className={`${status.bg} ${status.color} border-0 gap-1`}>
          <StatusIcon className="h-3 w-3" />
          {status.label}
        </Badge>
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-slate-500">{t("cpu")}</span>
            <span
              className={`font-medium ${
                server.cpu > 80
                  ? "text-red-600"
                  : server.cpu > 60
                    ? "text-amber-600"
                    : "text-slate-700"
              }`}
            >
              {server.cpu}%
            </span>
          </div>
          <Progress
            value={server.cpu}
            className={`h-1.5 ${
              server.cpu > 80
                ? "[&>div]:bg-red-500"
                : server.cpu > 60
                  ? "[&>div]:bg-amber-500"
                  : "[&>div]:bg-emerald-500"
            }`}
          />
        </div>

        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-slate-500">{t("memory")}</span>
            <span
              className={`font-medium ${
                server.memory > 85
                  ? "text-red-600"
                  : server.memory > 70
                    ? "text-amber-600"
                    : "text-slate-700"
              }`}
            >
              {server.memory}%
            </span>
          </div>
          <Progress
            value={server.memory}
            className={`h-1.5 ${
              server.memory > 85
                ? "[&>div]:bg-red-500"
                : server.memory > 70
                  ? "[&>div]:bg-amber-500"
                  : "[&>div]:bg-emerald-500"
            }`}
          />
        </div>

        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-slate-500">{t("disk")}</span>
            <span className="font-medium text-slate-700">{server.disk}%</span>
          </div>
          <Progress value={server.disk} className="h-1.5 [&>div]:bg-blue-500" />
        </div>
      </div>

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
        <div className="text-center">
          <p className="text-xs text-slate-500">{t("latency")}</p>
          <p className="font-semibold text-sm">{server.latency || 0}ms</p>
        </div>
        <div className="text-center">
          <p className="text-xs text-slate-500">{t("uptime")}</p>
          <p className="font-semibold text-sm text-emerald-600">
            {server.uptime || 0}%
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-slate-500">{t("reqPerMin")}</p>
          <p className="font-semibold text-sm">
            {Math.round((server.requests || 0) / 60)}
          </p>
        </div>
      </div>

      <div className="mt-3">
        <Button
          variant="outline"
          size="sm"
          className="w-full gap-2"
          onClick={(e) => onViewLogs(server, e)}
          disabled={!server.isDataReal}
        >
          <FileText className="h-4 w-4" />
          {t("viewLogs")}
        </Button>
      </div>
    </motion.div>
  );
}
